/**
 * Administrator console frame.
 *
 * The navigation is built from the capability list the server returned with the
 * session, so an account only sees the areas it can actually use. That is a
 * rendering decision, not a security one: every console endpoint re-checks the
 * capability, and a hand-typed URL fails there.
 */

import { NavLink, Navigate, Outlet } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { AdminCapability } from '@shared/constants'

import { LoadingBlock } from '../../components/layout/states'
import { useCurrentUser, useMe } from '../../hooks/use-auth'
import { useSessionStore } from '../../stores/session'
import { cn } from '../../lib/format'

interface NavItem {
  to: string
  labelKey: string
  capability?: AdminCapability
  /** Overview is the only entry every administrator may open. */
  anyAdmin?: boolean
}

const NAV: NavItem[] = [
  { to: '/admin', labelKey: 'admin.nav.overview', anyAdmin: true },
  // The data board matters most to whoever is diagnosing a failing round, so it
  // is available to every administrator rather than behind a capability.
  { to: '/admin/data-status', labelKey: 'admin.nav.dataStatus', anyAdmin: true },
  // The map board carries the switch an operator reaches for when a quota alert
  // arrives, so it is behind the capability that can actually change it.
  { to: '/admin/map-data', labelKey: 'admin.nav.mapData', capability: 'settings:read' },
  { to: '/admin/applications', labelKey: 'admin.nav.applications', capability: 'application:read' },
  { to: '/admin/codes', labelKey: 'admin.nav.codes', capability: 'code:read' },
  { to: '/admin/users', labelKey: 'admin.nav.users', capability: 'user:read' },
  { to: '/admin/referrals', labelKey: 'admin.nav.referrals', capability: 'referral:read' },
  { to: '/admin/admins', labelKey: 'admin.nav.admins', capability: 'admin:read' },
  { to: '/admin/settings', labelKey: 'admin.nav.settings', capability: 'settings:read' },
]

function tabClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'border-b-2 px-1 py-1.5 text-sm transition-colors',
    isActive
      ? 'border-primary text-foreground font-medium'
      : 'text-muted-foreground hover:text-foreground border-transparent',
  )
}

export function AdminLayout() {
  const { t } = useTranslation()
  const { capabilities } = useCurrentUser()
  const me = useMe()
  const user = useSessionStore((state) => state.user)

  if (me.isPending || (me.isError && !user)) {
    return (
      <div className="container-narrow">
        <LoadingBlock label={t('common.loading')} />
      </div>
    )
  }

  // No capability at all means this account has no business here; the console is
  // not advertised, so the visitor is simply sent back to their own area.
  if (capabilities.length === 0) {
    return <Navigate to="/me" replace />
  }

  const visible = NAV.filter(
    (item) => item.anyAdmin || (item.capability !== undefined && capabilities.includes(item.capability)),
  )

  return (
    <div className="flex flex-col">
      <div className="border-border border-b">
        <div className="container-narrow flex flex-wrap items-center gap-x-3 gap-y-1 py-1">
          {visible.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === '/admin'} className={tabClass}>
              {t(item.labelKey)}
            </NavLink>
          ))}
        </div>
      </div>
      <Outlet />
    </div>
  )
}
